import { useEffect } from 'react'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'

const Privacy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | BizMatch'
    const meta = document.querySelector('meta[name="description"]')
    if (meta) meta.setAttribute('content', 'Privacy Policy for BizMatch business marketplace.')
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-16 max-w-4xl">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-3xl font-bold text-primary mb-6">Privacy Policy – BizMatch</h1>
          
          <p className="text-muted-foreground mb-6">
            <strong>Effective Date:</strong> 24 November 2024
          </p>
          
          <p className="mb-6">
            BizMatch ("we", "our", "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our website, mobile application, and related services (together, "Services").
          </p>
          
          <p className="mb-8">
            By using our Services, you agree to this Privacy Policy.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">1. Information We Collect</h2>
          
          <h3 className="text-xl font-medium text-primary mb-3">(a) Personal Information</h3>
          <ul className="list-disc list-inside mb-4 space-y-1">
            <li>Name, email address, phone number, and business details.</li>
            <li>Login details created when registering for an account.</li>
            <li>Payment details (processed securely by third-party providers; we do not store full card details).</li>
          </ul>
          
          <h3 className="text-xl font-medium text-primary mb-3">(b) Business Listing Information</h3>
          <p className="mb-4">Business description, sector, location, financial details, and images uploaded by brokers or sellers.</p>
          
          <h3 className="text-xl font-medium text-primary mb-3">(c) Buyer Enquiry Information</h3>
          <p className="mb-4">Contact details and messages submitted through "Express Interest" or enquiry forms.</p>
          
          <h3 className="text-xl font-medium text-primary mb-3">(d) Technical Information</h3>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>Device details (type, operating system, browser).</li>
            <li>Log data (IP address, time and date of access).</li>
            <li>Cookies and analytics data (see Section 7).</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">2. How We Use Your Information</h2>
          <p className="mb-3">We may use your information to:</p>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>Provide, operate, and improve our Services.</li>
            <li>Facilitate account registration and management.</li>
            <li>Publish and manage business listings.</li>
            <li>Match buyers and sellers and forward enquiries to brokers/sellers.</li>
            <li>Process payments for listings and lead fees.</li>
            <li>Send service updates, confirmations, and notifications.</li>
            <li>Conduct analytics and research to improve BizMatch.</li>
            <li>Comply with legal and regulatory obligations.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">3. Disclosure of Information</h2>
          <p className="mb-3">We may disclose your personal information to:</p>
          <ul className="list-disc list-inside mb-4 space-y-1">
            <li>Other users of BizMatch (e.g., sellers/brokers receiving your enquiry).</li>
            <li>Our service providers (e.g., hosting, payment processing, email/SMS delivery, analytics).</li>
            <li>Regulators or law enforcement agencies where required by law.</li>
            <li>Business partners in the event of a merger, acquisition, or sale of assets.</li>
          </ul>
          <p className="mb-6">We do not sell your personal information to third parties.</p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">4. Data Storage & Security</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>We use secure hosting providers (including Supabase) to store data.</li>
            <li>We apply industry-standard security measures (encryption, access controls).</li>
            <li>No system is 100% secure; you use our Services at your own risk.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">5. Overseas Disclosure</h2>
          <p className="mb-6">
            Our service providers may store or process data outside Australia (for example, cloud hosting and payment processing providers). We take reasonable steps to ensure such providers comply with privacy standards comparable to the Australian Privacy Principles.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">6. Access & Correction</h2>
          <p className="mb-6">
            You may request access to personal information we hold about you and request correction if it is inaccurate, out-of-date, incomplete, or misleading. Please contact us using the details below.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">7. Cookies & Analytics</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>Our website and app may use cookies and similar technologies to enhance user experience and for analytics.</li>
            <li>We may use tools such as Google Analytics or PostHog to understand usage patterns.</li>
            <li>You can disable cookies in your browser; some features may not work as intended.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">8. Marketing Communications</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>We may send you emails or SMS about new features, listings, or offers.</li>
            <li>You can opt out at any time by clicking "unsubscribe" in our messages or by contacting us.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">9. Children's Privacy</h2>
          <p className="mb-6">
            BizMatch is not intended for children under 18. We do not knowingly collect personal information from children.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">10. Changes to This Policy</h2>
          <p className="mb-6">
            We may update this Privacy Policy from time to time. The latest version will always be posted on our website and app.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">11. Contact Us</h2>
          <p className="mb-3">
            If you have any questions, concerns, or complaints about this Privacy Policy or how we handle your data, please contact us:
          </p>
          <div className="bg-secondary/10 p-6 rounded-lg mb-6">
            <p className="font-medium mb-2">BizMatch</p>
            <p>Email: Hello@BizMatch.com.au</p>
            <p>Phone: +61 418 616 704</p>
            <p>Address: 2/290 Boundary St, Spring Hill, QLD 4000</p>
            <p>Website: bizmatch.com.au</p>
          </div>
          
          <p>
            If you are not satisfied with our response, you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" className="text-primary underline hover:text-primary/80" target="_blank" rel="noopener noreferrer">www.oaic.gov.au</a>.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Privacy
